import React, { useEffect, useState } from "react";
import { LoggedInView } from "../common/LoggedInView";
import { useAppDispatch, useAppSelector } from "../../utils/Hooks";
import { getSettings, updateSettings, resetUpdateSettingsStatus } from "../../store/reducers/AdvertisingSettingsSlice";
import { UpdateAdvertisingSetting } from "../../models/AdvertisingSetting";
import { RequestStatus } from "../../utils/Enums";
import Editor from 'react-simple-wysiwyg';
import SettingsCountryPicker from "../common/SettingsCountryPicker";
import SettingsDayPicker from "../common/SettingsDayPicker";

interface AdvertisingSettingsScreenProps {}

interface Stats {
    total_contacts: number;
    sent_count: number;
    current_position: string;
    current_cycle: number;
}

export const AdvertisingSettingsScreen: React.FC<AdvertisingSettingsScreenProps> = () => {
    const dispatch = useAppDispatch();
    const settingsState = useAppSelector((state) => state.advertisingSettings);
    const [updatedSettings, setUpdatedSettings] = useState<UpdateAdvertisingSetting[]>([]);
    const [updated, setUpdated] = useState(false);

    useEffect(() => {
        dispatch(getSettings());
    }, [dispatch]);

    useEffect(() => {
        if (settingsState.updateSettingsStatus === RequestStatus.Success) {
            setUpdated(true);
            setTimeout(() => {
                setUpdated(false);
                dispatch(resetUpdateSettingsStatus());
            }, 3000);
        }
    }, [settingsState.updateSettingsStatus, dispatch]);

    const handleInputChange = (identifier: string, value: string) => {
        const index = updatedSettings.findIndex((setting) => setting.identifier === identifier);
        const setting = settingsState.settings.find((s) => s.identifier === identifier);

        if (index !== -1) {
            const updatedSettingsCopy = [...updatedSettings];
            updatedSettingsCopy[index] = { ...updatedSettingsCopy[index], value, type: setting?.type || "text" };
            setUpdatedSettings(updatedSettingsCopy);
        } else {
            setUpdatedSettings([
                ...updatedSettings,
                {
                    identifier,
                    value,
                    type: setting?.type || "text",
                },
            ]);
        }
    };

    const handleToggleChange = (identifier: string, checked: boolean) => {
        const value = checked ? "true" : "false";
        handleInputChange(identifier, value);
    };

    const handleCountryChange = (identifier: string, countryIds: number[]) => {
        const value = JSON.stringify(countryIds);
        handleInputChange(identifier, value);
    };

    const handleDayChange = (identifier: string, dayIds: number[]) => {
        const value = JSON.stringify(dayIds);
        handleInputChange(identifier, value);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        dispatch(updateSettings(updatedSettings));
    };

    const stats: Stats = settingsState.stats || {
        total_contacts: 0,
        sent_count: 0,
        current_position: "A",
        current_cycle: 1,
    };

    return (
        <LoggedInView fullWidth={false}>
            {updated && (
                <div style={{ marginBottom: "20px", padding: "10px", backgroundColor: "#dff0d8", color: "#3c763d", borderRadius: "4px" }}>
                    <div>
                        <h2 style={{ fontSize: "18px", margin: "0" }}>Seaded uuendatud</h2>
                    </div>
                </div>
            )}
            {settingsState.errors.length > 0 && (
                <div style={{ marginBottom: "20px", padding: "10px", backgroundColor: "#f2dede", color: "#a94442", borderRadius: "4px" }}>
                    <div>
                        <h2 style={{ fontSize: "18px", margin: "0" }}>Viga seadete uuendamisel</h2>
                        <ul style={{ margin: "10px 0 0 20px", padding: "0" }}>
                            {settingsState.errors.map((error, index) => (
                                <li key={index} style={{ fontSize: "14px" }}>{error}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
            <div style={{ marginBottom: "20px", padding: "15px", backgroundColor: "#f8f8f8", borderRadius: "4px" }}>
                <h3 style={{ fontSize: "18px", margin: "0 0 10px 0" }}>Mailikampaania staatus</h3>
                <p style={{ fontSize: "14px", margin: "5px 0" }}>Kontaktide kogu arv: {stats.total_contacts}</p>
                <p style={{ fontSize: "14px", margin: "5px 0" }}>Saadetud arv: {stats.sent_count}</p>
                <p style={{ fontSize: "14px", margin: "5px 0" }}>
                    Praegune tähestiku positsioon: {stats.current_position || "A"} (Tsükkel {stats.current_cycle || 1})
                </p>
            </div>
            <div className="form">
                <h2 style={{ fontSize: "20px", margin: "0 0 15px 0" }}>
                    <span>Reklaamiseaded</span>
                </h2>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <tbody>
                    {settingsState?.settings.map((setting) => (
                        <tr key={setting.identifier} style={{ marginBottom: "10px" }}>
                            <th style={{ padding: "10px", textAlign: "left", fontWeight: "normal", width: "200px" }}>{setting.name}:</th>
                            <td style={{ padding: "10px" }}>
                                {setting.type === "wysiwyg" ? (
                                    <Editor
                                        value={
                                            updatedSettings.find((s) => s.identifier === setting.identifier)?.value ||
                                            setting.value
                                        }
                                        onChange={(e) => handleInputChange(setting.identifier, e.target.value)}
                                    />
                                ) : setting.type === "toggle" ? (
                                    <input
                                        type="checkbox"
                                        name={setting.identifier}
                                        checked={
                                            (updatedSettings.find((s) => s.identifier === setting.identifier)?.value ||
                                                setting.value) === "true"
                                        }
                                        onChange={(e) => handleToggleChange(setting.identifier, e.target.checked)}
                                    />
                                ) : setting.type === "countryPicker" ? (
                                    <SettingsCountryPicker
                                        value={JSON.parse(
                                            updatedSettings.find((s) => s.identifier === setting.identifier)?.value ||
                                            setting.value ||
                                            "[]"
                                        )}
                                        onChange={(countryIds) => handleCountryChange(setting.identifier, countryIds)}
                                    />
                                ) : setting.type === "dayPicker" ? (
                                    <SettingsDayPicker
                                        value={JSON.parse(
                                            updatedSettings.find((s) => s.identifier === setting.identifier)?.value ||
                                            setting.value ||
                                            "[]"
                                        )}
                                        onChange={(dayIds) => handleDayChange(setting.identifier, dayIds)}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        name={setting.identifier}
                                        style={{ width: "100%", padding: "5px", fontSize: "14px" }}
                                        value={
                                            updatedSettings.find((s) => s.identifier === setting.identifier)?.value ||
                                            setting.value
                                        }
                                        onChange={(e) => handleInputChange(setting.identifier, e.target.value)}
                                    />
                                )}
                            </td>
                        </tr>
                    ))}
                    <tr>
                        <th></th>
                        <td style={{ padding: "10px" }}>
                            <button
                                type="submit"
                                onClick={handleSubmit}
                            >
                  <span>
                    <strong>Muuda</strong>
                  </span>
                            </button>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </LoggedInView>
    );
};